/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, forwardRef} from 'react'
import PropTypes from 'prop-types'
import {useController} from 'react-hook-form'
import {
    Flex,
    FormControl,
    FormLabel,
    FormErrorMessage,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    InputLeftElement,
    Select,
    useMergeRefs,
    Radio,
    RadioGroup,
    Wrap,
    Textarea,
    WrapItem
} from '@chakra-ui/react'
import {
    ArrowIcon,
    CheckIcon,
    InfoIconFilled,
    TooltipIcon,
    VisibilityIcon,
    VisibilityOffIcon,
    WarningIcon
} from '../icons'
import Checkbox from '../checkbox'
import {trackFormField} from '../../tracking/analytics'
import {useKeyboardFocus} from '../../hooks/use-keyboard-focus'
import {useCurrency} from '../../hooks'
import {InfoIconTooltip} from '../info-icon-tooltip'
import DatePicker from '../date-picker'

const Field = forwardRef(
    (
        {
            name,
            label,
            formLabel,
            type = 'text',
            options = [],
            rules = {},
            error,
            placeholder,
            inputProps,
            control,
            defaultValue,
            helpText,
            helpTextInfoIcon,
            children,
            analyticsEvent,
            rightElement,
            leftElement,
            labelProps,
            productItems,
            id,
            inputCounterVisible,
            maxLength,
            isDisabled = false,
            suppressErrorMessage = false
        },
        inputRef
    ) => {
        const [hidePassword, setHidePassword] = useState(true)

        const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
        const inputType =
            type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type
        const isFieldset = ['checkbox', 'radio'].includes(type)
        const {
            field: {onChange, value, ref}
        } = useController({name, control, rules, defaultValue})
        const combinedRef = useMergeRefs(ref, inputRef)
        const accepted = value && !error
        const inputVariant = accepted ? 'accepted' : 'flushed'
        const _inputProps =
            typeof inputProps === 'function' ? inputProps({value, onChange, error}) : inputProps
        const {
            focusVariant: inputFocusVariant,
            onBlur: onInputBlur,
            onFocus: onInputFocus,
            onMouseEvent: onInputMouseEvent
        } = useKeyboardFocus({variant: inputVariant})
        const {
            focusVariant: radioFocusVariant,
            onBlur: onRadioBlur,
            onFocus: onRadioFocus,
            onMouseEvent: onRadioMouseEvent
        } = useKeyboardFocus({variant: 'field'})

        const {currency} = useCurrency()

        const Icon = helpTextInfoIcon ? InfoIconFilled : TooltipIcon

        return (
            <FormControl
                id={id ? id : name}
                isInvalid={error}
                onBlur={() =>
                    trackFormField(analyticsEvent, name, error, value, currency, productItems)
                }
                data-testid={`${name}-field-testid`}
                as={isFieldset ? 'fieldset' : undefined}
                minWidth={0}
            >
                {!['checkbox', 'hidden'].includes(type) &&
                    (formLabel ||
                        (label && (
                            <FormLabel as={isFieldset ? 'legend' : undefined} {...labelProps}>
                                <span>{label}</span>
                                {helpText && (
                                    <InfoIconTooltip name={name} helpText={helpText} icon={Icon} />
                                )}
                            </FormLabel>
                        )))}
                <InputGroup
                    // the date picker is displayed as child of the input group and needs not to be isolated or it will
                    // be rendered bellow next elements
                    isolation={type === 'date' ? 'auto' : null}
                >
                    {leftElement && <InputLeftElement w="min">{leftElement}</InputLeftElement>}
                    {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(type) && (
                        <>
                            <Input
                                ref={combinedRef}
                                onChange={onChange}
                                value={value}
                                type={inputType}
                                placeholder={placeholder}
                                variant={inputFocusVariant}
                                onMouseDown={onInputMouseEvent}
                                onFocus={onInputFocus}
                                onBlur={onInputBlur}
                                autoComplete={inputProps?.autoComplete || type}
                                {..._inputProps}
                            />
                        </>
                    )}

                    {type === 'date' && (
                        <DatePicker
                            ref={combinedRef}
                            onChange={onChange}
                            value={value}
                            placeholder={placeholder}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                            inputProps={{
                                variant: inputFocusVariant,
                                ..._inputProps,
                                onMouseDown: onInputMouseEvent
                            }}
                        />
                    )}

                    {type === 'textarea' && (
                        <Textarea
                            ref={combinedRef}
                            onChange={onChange}
                            value={value}
                            placeholder={placeholder}
                            variant={inputFocusVariant}
                            onMouseDown={onInputMouseEvent}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                            {..._inputProps}
                        />
                    )}

                    {type === 'hidden' && (
                        <input
                            ref={combinedRef}
                            onChange={onChange}
                            value={value}
                            type="hidden"
                            {..._inputProps}
                        />
                    )}
                    <InputRightElement w="min">
                        <HStack spacing={0}>
                            {type === 'password' && (
                                <IconButton
                                    minWidth={8}
                                    variant="ghosted"
                                    aria-label="Show password"
                                    icon={<PasswordIcon boxSize={4} />}
                                    aria-pressed={!hidePassword}
                                    onClick={() => setHidePassword(!hidePassword)}
                                />
                            )}
                            <span>{rightElement ? rightElement : null}</span>
                            {error && type !== 'hidden' && !rightElement && (
                                <Flex paddingRight={2}>
                                    <WarningIcon variant="rejected" aria-hidden="true" />
                                </Flex>
                            )}
                            {['text', 'email', 'phone', 'tel', 'number'].includes(type) &&
                                !rightElement &&
                                accepted && (
                                    <Flex paddingRight={2}>
                                        <CheckIcon variant="accepted" aria-hidden="true" />
                                    </Flex>
                                )}
                        </HStack>
                    </InputRightElement>

                    {type === 'select' && (
                        <Select
                            ref={combinedRef}
                            onChange={onChange}
                            value={value}
                            placeholder={placeholder}
                            icon={<ArrowIcon />}
                            variant={inputFocusVariant}
                            onMouseDown={onInputMouseEvent}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                            {..._inputProps}
                        >
                            {options.map((opt) => (
                                <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Select>
                    )}

                    {type === 'checkbox' && (
                        <HStack spacing={1}>
                            <Checkbox
                                ref={combinedRef}
                                onChange={(e) => {
                                    onChange(e.target.checked)
                                }}
                                isChecked={value}
                                isDisabled={isDisabled}
                                {..._inputProps}
                            >
                                {formLabel || label}
                            </Checkbox>
                            {helpText && (
                                <InfoIconTooltip
                                    name={name}
                                    helpText={helpText}
                                    icon={InfoIconFilled}
                                    iconProps={{
                                        color: 'stokkeGray.mid'
                                    }}
                                />
                            )}
                        </HStack>
                    )}

                    {type === 'radio' && (
                        <RadioGroup
                            defaultValue={defaultValue}
                            onChange={onChange}
                            value={value}
                            my={2}
                            onClick={onRadioMouseEvent}
                            onFocus={onRadioFocus}
                            onBlur={onRadioBlur}
                            variant={radioFocusVariant}
                        >
                            <Wrap spacing={6}>
                                {options.map((opt) => (
                                    <WrapItem key={`${opt.label}-${opt.value}`}>
                                        <Radio value={opt.value}>{opt.label}</Radio>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        </RadioGroup>
                    )}

                    {children}
                </InputGroup>
                {inputCounterVisible && (
                    <Flex
                        justify="end"
                        textStyle="Regular Small"
                        color={'stokkeCore.accessibleGrey'}
                        mt="1"
                    >
                        {value?.length}/{maxLength}
                    </Flex>
                )}
                {error?.message && !type !== 'hidden' && !suppressErrorMessage && (
                    <FormErrorMessage>{error.message}</FormErrorMessage>
                )}
            </FormControl>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Field.displayName = 'Field'
}

Field.propTypes = {
    name: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden',
        'radio',
        'textarea',
        'date'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    helpTextInfoIcon: PropTypes.bool,
    children: PropTypes.any,
    analyticsEvent: PropTypes.string,
    rightElement: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    leftElement: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    labelProps: PropTypes.object,
    productItems: PropTypes.array,
    inputCounterVisible: PropTypes.bool,
    maxLength: PropTypes.number,
    isDisabled: PropTypes.bool,
    suppressErrorMessage: PropTypes.bool
}

export default Field
